import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, DetailedHTMLProps } from 'react';
import { createUseStyles } from 'react-jss';

import type { LcpnTheme } from '../../styles/theme';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  button: {
    outline: 'none',
    border: 'none',
    backgroundColor: 'var(--btn-normal-color)',
    color: 'var(--btn-normal-text)',
    borderRadius: 20,
    minHeight: theme.spacing(9),
    height: '100%',
    padding: theme.spacing(1, 4),
    cursor: 'pointer',
    fontSize: theme.fontSizes.p2,
    fontWeight: 400,
    '&:hover': {
      backgroundColor: 'var(--btn-hover-color)',
      color: 'var(--btn-hover-text)',
    },
  },

  active: {
    backgroundColor: 'var(--btn-selected-color)',
    color: 'var(--btn-selected-text)',
    padding: theme.spacing(0, 4),
    '&:hover': {
      backgroundColor: 'var(--btn-selected-color)',
      color: 'var(--btn-selected-text)',
    },
  },

  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'inherit',
  },

  [`@media screen and (max-width: ${theme.breakPoints.widescreen}px)`]: {
    button: {
      '&&': {
        minHeight: theme.spacing(8),
        padding: theme.spacing(1, 2),
      },
    },
  },
}));

export interface ButtonProps
  extends DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  label: string;
  children?: JSX.Element;
  selected?: boolean;
  bgColor?: string;
  normalColor?: string;
  hoverColor?: string;
  textColor?: string;
  normalTextColor?: string;
  hoverTextColor?: string;
}

export function Button(props: ButtonProps) {
  const {
    children,
    label,
    selected,
    bgColor,
    normalColor,
    hoverColor,
    textColor,
    normalTextColor,
    hoverTextColor,
    style,
    ...htmlButtonProps
  } = props;
  const classes = useStyles();

  const cssVars = {
    '--btn-normal-color': normalColor
      ? `rgb(${normalColor})`
      : `rgb(${bgColor})`,
    '--btn-hover-color': hoverColor ? `rgb(${hoverColor})` : `rgb(${bgColor})`,
    '--btn-selected-color': bgColor ? `rgb(${bgColor})` : '#008b7f',
    '--btn-normal-text': normalTextColor || textColor || '#ffffff',
    '--btn-hover-text':
      hoverTextColor || normalTextColor || textColor || '#ffffff',
    '--btn-selected-text': textColor || '#ffffff',
    ...style,
  } as React.CSSProperties;

  return (
    <button
      className={clsx(classes.button, { [classes.active]: selected })}
      style={cssVars}
      {...htmlButtonProps}
    >
      <div className={classes.buttonGroup}>
        {children}
        <span>{label}</span>
      </div>
    </button>
  );
}
